import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { type MemberInfo, MemberStatus, type RemainingBalance } from '@/typings/account'
import { MemberLevel, MemberMaxTaskCount } from '@/constants/account'
import type { CouponInfo } from '@/typings/reward-task'
import { IS_INTL } from '@/constants/global'
import type { AccountPlugin } from '@/typings/plugins'

export const useAccountStore = defineStore(
  'account',
  () => {
    const token = ref<string>('')
    const codeSentTime = ref<number>(0)
    const userId = ref<string>()
    const balance = ref<number>(0)
    const remainMe = ref<boolean>(false)
    const remainMeInfo = ref<{ username: string; password: string }>()
    const nickname = ref<string>('')
    const avatar = ref<string>('')
    const userEmail = ref<string>('')
    const invited = ref<boolean>(false)
    const phone = ref<string>('')
    const memberInfo = ref<MemberInfo | null>(null)
    const remainingBalance = ref<RemainingBalance | null>(null)
    const memberStatus = ref<MemberStatus>(MemberStatus.NORMAL)
    const guideTaskAssigned = ref<boolean>(false)
    const guideTaskExpired = ref<boolean>(true)
    const freeUsage = ref<{ count: number; limit: number; resetAt: number }>({
      count: 0,
      limit: 0,
      resetAt: 0
    })
    const couponList = ref<CouponInfo[]>([])
    const accountPlugins = ref<AccountPlugin[]>([])

    const maxTaskCount = computed(() => {
      if (!memberInfo.value || !memberInfo.value.userMember) return 2

      const memberId = memberInfo.value.userMember.member.id

      return MemberMaxTaskCount[memberId]
    })

    // TODO 如果往后会员等级有变动，需要修改这里，可能最高可购买会员不是专业版
    // 是否升级场景，非国际版，用户有会员，且会员等级大于等于免费版，小于专业版
    const isUpgrade = computed(
      () =>
        !IS_INTL &&
        memberInfo.value?.userMember &&
        memberInfo.value?.userMember?.member?.id >= MemberLevel.FREE &&
        memberInfo.value?.userMember?.member?.id < MemberLevel.PROFESSIONAL
    )
    // 拥有折扣
    const discount = computed(() => {
      // 返回折扣券中折扣最大的数值
      return (couponList.value || []).reduce((max, coupon) => {
        return Math.max(max, coupon.discountRate)
      }, 0)
    })

    const setToken = (t: string) => (token.value = t)
    const setCodeSentTime = (time: number) => (codeSentTime.value = time)
    const setUserId = (uid: string) => (userId.value = uid)
    const setBalance = (b: number) => (balance.value = b)
    const setRemainMe = (remain: boolean) => (remainMe.value = remain)
    const setRemainMeInfo = (
      loginParams:
        | {
            username: string
            password: string
          }
        | undefined
    ) => (remainMeInfo.value = loginParams)
    const setNickname = (name: string) => (nickname.value = name)
    const setAvatar = (url: string) => (avatar.value = url)
    const setEmail = (email: string) => (userEmail.value = email)
    const setInvited = (i: boolean) => (invited.value = i)
    const setPhone = (p: string) => (phone.value = p)
    const setMemberInfo = (info: MemberInfo | null) => (memberInfo.value = info)
    const setRemainingBalance = (balance: RemainingBalance) => (remainingBalance.value = balance)
    const setMemberStatus = (status: MemberStatus) => (memberStatus.value = status)
    const setGuideTaskAssigned = (assigned: boolean) => (guideTaskAssigned.value = assigned)
    const setGuideTaskExpired = (expired: boolean) => (guideTaskExpired.value = expired)
    const setFreeUsage = (usage: { count: number; limit: number; resetAt: number }) =>
      (freeUsage.value = usage)
    const setCouponList = (list: CouponInfo[]) => (couponList.value = list)
    const setAccountPlugins = (plugins: AccountPlugin[]) => (accountPlugins.value = plugins)

    return {
      token,
      codeSentTime,
      userId,
      userEmail,
      balance,
      remainMeInfo,
      remainMe,
      nickname,
      avatar,
      invited,
      phone,
      memberInfo,
      remainingBalance,
      maxTaskCount,
      memberStatus,
      guideTaskAssigned,
      guideTaskExpired,
      freeUsage,
      couponList,
      discount,
      isUpgrade,
      accountPlugins,
      setToken,
      setCodeSentTime,
      setUserId,
      setBalance,
      setRemainMeInfo,
      setRemainMe,
      setNickname,
      setAvatar,
      setEmail,
      setInvited,
      setPhone,
      setMemberInfo,
      setMemberStatus,
      setGuideTaskAssigned,
      setGuideTaskExpired,
      setFreeUsage,
      setCouponList,
      setRemainingBalance,
      setAccountPlugins
    }
  },
  {
    persist: {
      paths: ['token', 'codeSentTime', 'remainMe', 'remainMeInfo', 'userId', 'userEmail']
    }
  }
)
